import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { DialogBox } from "../../../common/components/functional/dialogBox/DialogBox";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import { CustomerContract } from "../../customer/customerAPI";
import { EezyCompany, InvoiceEdit, TYPE_EXTERNAL } from "../invoiceAPI";
import { saveLocalInvoiceDraft } from "../invoiceDetailedSlice";
import { fetchEezyCompanies } from "../invoiceSlice";
import { InvoiceDialogAdditionalInfo } from "./InvoiceDialogAdditionalInfo";
import { InvoiceDialogBasicInfo } from "./InvoiceDialogBasicInfo";
import { emptyCustomerContract } from "../../customer/contract/helpers/contractHelpers";
import { isoFormat } from "../../../common/helpers/formatDateTime";

interface Props {
  isOpen: boolean;
  closeDialog: () => void;
}

const initEezyCompany = {
  eezyCompanyId: "",
  name: "",
  businessId: "",
  isFranchise: false,
  costCenters: [],
};

const initCustomer: CustomerOrgCompany = {
  id: "",
  name: "",
  customerOrganizations: [],
};

const initInvoiceRow = {
  id: nanoid(),
  eezyCostCenterId: "",
  salesAccountCode: "",
  quantityAmount: "0",
  quantityUnit: "",
  unitPriceAmount: "0.00",
  customerOrganizationId: "",
  customerCostCenterId: "",
  customerCostCenterCode: "",
  vatRatePercent: "0",
  vatCode: "",
  vatExcludedAmount: "",
  vatAmount: "",
  amount: "",
  articleName: "",
  articleCategory: "",
  freeText: "",
  editable: true,
};

export const NewInvoiceDialog = ({ isOpen, closeDialog }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("invoices");
  const { status } = useAppSelector((state) => state.invoicesDetailed);
  const isLoading = status === "pending";

  const [newInvoice, setNewInvoice] = useState<InvoiceEdit>();

  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );

  const [seller, setSeller] = useState<EezyCompany>(initEezyCompany);
  const [invoiceType, setInvoiceType] = useState(TYPE_EXTERNAL);
  const [customer, setCustomer] = useState<CustomerOrgCompany>(initCustomer);
  const [customerContract, setCustomerContract] = useState<CustomerContract>(
    emptyCustomerContract,
  );
  const [eezyBuyer, setEezyBuyer] = useState<EezyCompany>(initEezyCompany);
  const [valueDate, setValueDate] = useState<Date | null>(null);

  const clearBasicInfo = () => {
    setSeller(initEezyCompany);
    setInvoiceType(TYPE_EXTERNAL);
  };

  const clearAdditionalInfo = () => {
    setCustomer(initCustomer);
    setCustomerContract(emptyCustomerContract);
    setEezyBuyer(initEezyCompany);
    setValueDate(null);
  };

  const selectSeller = (company: EezyCompany) => {
    setSeller(company || initEezyCompany);
  };

  const setType = (type: string) => {
    setInvoiceType(type);
  };

  const selectCustomer = (customer: CustomerOrgCompany | null) => {
    setCustomer(customer || initCustomer);
  };

  const selectCustomerContract = useCallback((contract: CustomerContract) => {
    setCustomerContract(contract);
  }, []);

  const selectEezyBuyer = (buyer: EezyCompany) => {
    setEezyBuyer(buyer || initEezyCompany);
  };

  const selectValueDate = (date: Date | null) => {
    setValueDate(date);
  };

  const steps = [
    {
      label: t("invoiceType"),
      component: (
        <InvoiceDialogBasicInfo
          eezyCompany={seller}
          invoiceType={invoiceType}
          eezyCompanyOptions={eezyCompanyOptions}
          setEezyCompany={selectSeller}
          setInvoiceType={setType}
        />
      ),
      disableNext: seller.eezyCompanyId === "",
    },
    {
      label: t("additionalInfo"),
      component: (
        <InvoiceDialogAdditionalInfo
          sellerId={seller.eezyCompanyId}
          customer={customer}
          selectCustomer={selectCustomer}
          invoiceType={invoiceType}
          customerContract={customerContract}
          selectContract={selectCustomerContract}
          eezyBuyerOptions={eezyCompanyOptions.filter(
            (eezyBuyer) => eezyBuyer.eezyCompanyId !== seller.eezyCompanyId,
          )}
          eezyBuyer={eezyBuyer}
          selectEezyBuyer={selectEezyBuyer}
          valueDate={valueDate}
          selectValueDate={selectValueDate}
        />
      ),
      disableNext:
        invoiceType === TYPE_EXTERNAL
          ? customer.id === "" || customerContract.id === "" || !valueDate
          : eezyBuyer.eezyCompanyId === "" || !valueDate,
    },
  ];

  const finishDialog = () => {
    closeDialog();
    const defaultDeliveryDate = customerContract.invoiceDeliveryDate
      ? format(valueDate!!, isoFormat)
      : null;
    const invoiceBase = {
      // entityadapter needs an id but theres only be one new invoice at a time so "" is fine
      id: "",
      eezyCompanyId: seller.eezyCompanyId,
      eezyCompanyName: seller.name,
      invoiceDate: format(new Date(), isoFormat),
      invoiceDueDate: "",
      invoiceValueDate: format(valueDate!!, isoFormat),
      invoiceStatus: "",
      invoiceType: invoiceType,
      seller: undefined,
      buyer: undefined,
      sellersBuyerIdentifier: null,
      originalInvoiceNumber: null,
      bundleId: null,
      freeText: null,
      breakDownRows: null,
      deliveryDate: defaultDeliveryDate,
      language: customerContract.invoiceLanguage,
    };
    setNewInvoice(
      invoiceType === TYPE_EXTERNAL
        ? {
            ...invoiceBase,
            customerOrganizationId: customer.customerOrganizations[0].id,
            customerOrganizationName: customer.customerOrganizations[0].name,
            customerCompanyId: customer.id,
            contractId: customerContract.id,
            paymentTermDays: customerContract.paymentTermDays?.toString() || "",
            sellerReferenceIdentifier:
              customerContract.invoiceReferences?.sellerReferenceIdentifier,
            buyerReferenceIdentifier:
              customerContract.invoiceReferences?.buyerReferenceIdentifier,
            orderIdentifier:
              customerContract.invoiceReferences?.orderIdentifier,
            agreementIdentifier:
              customerContract.invoiceReferences?.agreementIdentifier,
            eezyBuyerCompanyId: undefined,
            eezyBuyerCompanyName: undefined,
            invoiceRows: [
              {
                ...initInvoiceRow,
                salesAccountCode: customerContract.salesAccount?.code || "",
                vatCode: customerContract.salesAccount?.defaultVatCode || "",
              },
            ],
          }
        : {
            ...invoiceBase,
            eezyBuyerCompanyId: eezyBuyer.eezyCompanyId,
            eezyBuyerCompanyName: eezyBuyer.name,
            paymentTermDays: "",
            contractId: undefined,
            customerOrganizationId: undefined,
            customerOrganizationName: undefined,
            invoiceRows: [initInvoiceRow],
          },
    );
  };

  const cancelDialog = () => {
    closeDialog();
    clearBasicInfo();
    clearAdditionalInfo();
  };

  useEffect(() => {
    clearAdditionalInfo();
  }, [invoiceType]);

  useEffect(() => {
    if (!!newInvoice) {
      dispatch(
        saveLocalInvoiceDraft({
          ...newInvoice,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          clearAdditionalInfo();
          navigate("/home/invoices/new");
        }
      });
    }
  }, [newInvoice, dispatch, navigate]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchEezyCompanies({})).then(
        (action) =>
          isFulfilled(action) && setEezyCompanyOptions(action.payload),
      );
    }
  }, [isOpen, dispatch]);

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <DialogBox
        isOpen={isOpen}
        dialogTitle={t("addNewInvoice")}
        onCancelDialog={cancelDialog}
        onFinishDialog={finishDialog}
        steps={steps}
        cancelLabel={t("common:cancel")}
        finishLabel={t("createInvoice")}
      />
    </>
  );
};
